import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	Workforce,
	BENCH_REASONS,
	BILLING_TYPES,
	LOCATION_TYPES,
	CONTRACT_TYPES,
	CURRENCY_OPTIONS,
	calculateMargin,
} from "@/types/workforce";
import {
	Building,
	Calendar,
	DollarSign,
	MapPin,
	FileText,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface WorkforceEditTabsProps {
	workforce: Workforce;
	onChange: (field: string, value: any) => void;
}

export const WorkforceEditTabs = ({ workforce, onChange }: WorkforceEditTabsProps) => {
	const { t } = useTranslation();
	const [activeTab, setActiveTab] = useState<'assignment' | 'work' | 'financial'>('assignment');

	const isBench = !!workforce.is_bench;

	// Job type is read-only - determined at creation
	const useInternalJob = !!workforce.job_id;

	// Calculate margins
	const dailyMargin = useMemo(() => {
		return calculateMargin(workforce.purchase_price_daily, workforce.selling_price_daily);
	}, [workforce.purchase_price_daily, workforce.selling_price_daily]);

	const hourlyMargin = useMemo(() => {
		return calculateMargin(workforce.purchase_price_hourly, workforce.selling_price_hourly);
	}, [workforce.purchase_price_hourly, workforce.selling_price_hourly]);

	// Bench edit form - simplified
	if (isBench) {
		return (
			<div className="space-y-5">
				{/* Bench Start Date */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700">
							<Calendar className="h-4 w-4 inline mr-1" />
							{t("workforce.bench.startDate", "Bench Start Date")}
						</Label>
						<Input
							type="date"
							value={workforce.start_date || ""}
							onChange={(e) => onChange("start_date", e.target.value)}
							className="h-11"
						/>
					</div>
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700">
							<Calendar className="h-4 w-4 inline mr-1" />
							{t("workforce.fields.endDate", "End Date")}
						</Label>
						<Input
							type="date"
							value={workforce.end_date || ""}
							onChange={(e) => onChange("end_date", e.target.value)}
							className="h-11"
						/>
					</div>
				</div>

				{/* Bench Reason */}
				<div className="space-y-2">
					<Label className="text-sm font-medium text-gray-700">{t("workforce.bench.reason", "Reason")}</Label>
					<Select
						value={workforce.bench_reason || ""}
						onValueChange={(value) => onChange("bench_reason", value)}
					>
						<SelectTrigger className="h-11">
							<SelectValue placeholder={t("workforce.bench.selectReason", "Select reason...")} />
						</SelectTrigger>
						<SelectContent>
							{BENCH_REASONS.map((reason) => (
								<SelectItem key={reason.value} value={reason.value}>
									{t(`workforce.bench.reasons.${reason.value}`, reason.label)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{/* Daily Cost (mandatory) & Currency */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700">
							<DollarSign className="h-4 w-4 inline mr-1" />
							{t("workforce.bench.dailyCostLabel", "Daily Cost (what you pay them)")} *
						</Label>
						<Input
							type="number"
							min="0"
							step="0.01"
							value={workforce.bench_daily_cost ?? ""}
							onChange={(e) => onChange("bench_daily_cost", e.target.value ? parseFloat(e.target.value) : null)}
							placeholder="0.00"
							className="h-11"
						/>
						<p className="text-xs text-muted-foreground">{t("workforce.bench.dailyCostHint", "Used to calculate bench costs over time")}</p>
					</div>
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.currency", "Currency")}</Label>
						<Select
							value={workforce.currency || "EUR"}
							onValueChange={(value) => onChange("currency", value)}
						>
							<SelectTrigger className="h-11">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{CURRENCY_OPTIONS.map((currency) => (
									<SelectItem key={currency} value={currency}>
										{currency}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				{/* Allocation % */}
				<div className="space-y-2">
					<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.workPercentage", "Allocation %")}</Label>
					<Input
						type="number"
						min="0"
						max="100"
						value={workforce.work_percentage || ""}
						onChange={(e) => onChange("work_percentage", e.target.value ? parseInt(e.target.value) : null)}
						placeholder="100"
						className="h-11"
					/>
				</div>

				{/* Notes */}
				<div className="space-y-2">
					<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.notes", "Notes")}</Label>
					<Textarea
						value={workforce.notes || ""}
						onChange={(e) => onChange("notes", e.target.value)}
						placeholder={t("workforce.bench.notesPlaceholder", "Additional notes about this bench entry...")}
						rows={4}
						className="resize-none"
					/>
				</div>
			</div>
		);
	}

	// Regular assignment edit form - 3 tabs
	return (
		<Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'assignment' | 'work' | 'financial')} className="w-full">
			<TabsList className="grid grid-cols-3 mb-6">
				<TabsTrigger value="assignment" className="flex items-center gap-2">
					<FileText className="h-4 w-4" />
					<span className="hidden sm:inline">{t("workforce.edit.tabs.assignment", "Assignment")}</span>
				</TabsTrigger>
				<TabsTrigger value="work" className="flex items-center gap-2">
					<MapPin className="h-4 w-4" />
					<span className="hidden sm:inline">{t("workforce.edit.tabs.work", "Work Details")}</span>
				</TabsTrigger>
				<TabsTrigger value="financial" className="flex items-center gap-2">
					<DollarSign className="h-4 w-4" />
					<span className="hidden sm:inline">{t("workforce.edit.tabs.financial", "Financial")}</span>
				</TabsTrigger>
			</TabsList>

			{/* Assignment Tab */}
			<TabsContent value="assignment" className="space-y-5">
				{/* Job Type - Read-only */}
				<div className="p-4 border rounded-sm bg-gray-50">
					<div className="flex items-center justify-between">
						<div>
							<Label className="text-sm font-medium">{t("workforce.edit.jobType", "Assignment Type")}</Label>
							<p className="text-sm text-muted-foreground mt-1">
								{useInternalJob
									? t("workforce.edit.internalJob", "Linked to existing job in system")
									: t("workforce.edit.externalJob", "External assignment (client not in system)")}
							</p>
						</div>
					</div>
					<p className="text-xs text-muted-foreground mt-2 italic">
						{t("workforce.edit.jobReadOnly", "The job linked to an assignment cannot be changed after creation.")}
					</p>
				</div>

				{/* Job Details - Read-only */}
				{useInternalJob ? (
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700">
							{t("workforce.fields.job", "Job")}
						</Label>
						{workforce.job && (
							<div className="flex items-center gap-2 p-2 bg-blue-50 rounded-sm border border-blue-200">
								<Building className="h-4 w-4 text-blue-500" />
								<span className="text-sm font-medium text-blue-700">
									{workforce.job.title} {workforce.job.company?.name && `@ ${workforce.job.company.name}`}
								</span>
							</div>
						)}
					</div>
				) : (
					<div className="space-y-4 p-4 border rounded-sm bg-muted/50">
						<div className="flex items-center gap-2 mb-2">
							<Building className="h-4 w-4 text-muted-foreground" />
							<span className="text-sm font-semibold">{t("workforce.edit.externalDetails", "External Client Details")}</span>
						</div>
						<div className="space-y-2">
							<Label className="text-sm font-medium">{t("workforce.fields.externalCompany", "Company Name")}</Label>
							<Input
								value={workforce.external_company_name || ""}
								onChange={(e) => onChange("external_company_name", e.target.value)}
								className="h-11"
							/>
						</div>
						<div className="space-y-2">
							<Label className="text-sm font-medium">{t("workforce.fields.externalJobTitle", "Role/Position")}</Label>
							<Input
								value={workforce.external_job_title || ""}
								onChange={(e) => onChange("external_job_title", e.target.value)}
								className="h-11"
							/>
						</div>
						<div className="space-y-2">
							<Label className="text-sm font-medium">{t("workforce.fields.clientContact", "Client Contact")}</Label>
							<Input
								value={workforce.external_company_contact || ""}
								onChange={(e) => onChange("external_company_contact", e.target.value)}
								className="h-11"
							/>
						</div>
					</div>
				)}

				{/* Dates */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700">
							<Calendar className="h-4 w-4 inline mr-1" />
							{t("workforce.fields.startDate", "Start Date")}
						</Label>
						<Input
							type="date"
							value={workforce.start_date || ""}
							onChange={(e) => onChange("start_date", e.target.value)}
							className="h-11"
						/>
					</div>
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700">
							<Calendar className="h-4 w-4 inline mr-1" />
							{t("workforce.fields.expectedEndDate", "Expected End Date")}
						</Label>
						<Input
							type="date"
							value={workforce.expected_end_date || ""}
							onChange={(e) => onChange("expected_end_date", e.target.value)}
							className="h-11"
						/>
					</div>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700">
							<Calendar className="h-4 w-4 inline mr-1" />
							{t("workforce.fields.endDate", "End Date")}
						</Label>
						<Input
							type="date"
							value={workforce.end_date || ""}
							onChange={(e) => onChange("end_date", e.target.value)}
							className="h-11"
						/>
					</div>
				</div>
			</TabsContent>

			{/* Work Details Tab */}
			<TabsContent value="work" className="space-y-5">
				{/* Location */}
				<div className="space-y-2">
					<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.location", "Work Location")}</Label>
					<Input
						value={workforce.location || ""}
						onChange={(e) => onChange("location", e.target.value)}
						placeholder={t("workforce.placeholders.location", "City, office, or remote")}
						className="h-11"
					/>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					{/* Location Type */}
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.locationType", "Work Mode")}</Label>
						<Select
							value={workforce.location_type || ""}
							onValueChange={(value) => onChange("location_type", value)}
						>
							<SelectTrigger className="h-11">
								<SelectValue placeholder={t("workforce.placeholders.selectWorkMode", "Select...")} />
							</SelectTrigger>
							<SelectContent>
								{LOCATION_TYPES.map((type) => (
									<SelectItem key={type.value} value={type.value}>
										{type.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{/* Work Percentage */}
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.workPercentage", "Allocation %")}</Label>
						<Input
							type="number"
							min="0"
							max="100"
							value={workforce.work_percentage || ""}
							onChange={(e) => onChange("work_percentage", e.target.value ? parseInt(e.target.value) : null)}
							placeholder="100"
							className="h-11"
						/>
					</div>
				</div>

				{/* Contract Type */}
				<div className="space-y-2">
					<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.contractType", "Contract Type")}</Label>
					<Select
						value={workforce.contract_type || ""}
						onValueChange={(value) => onChange("contract_type", value)}
					>
						<SelectTrigger className="h-11">
							<SelectValue placeholder={t("workforce.placeholders.selectContract", "Select...")} />
						</SelectTrigger>
						<SelectContent>
							{CONTRACT_TYPES.map((type) => (
								<SelectItem key={type.value} value={type.value}>
									{type.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{/* Contract Reference */}
				<div className="space-y-2">
					<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.contractReference", "Contract/PO Reference")}</Label>
					<Input
						value={workforce.contract_reference || ""}
						onChange={(e) => onChange("contract_reference", e.target.value)}
						placeholder={t("workforce.placeholders.contractRef", "Contract number or PO")}
						className="h-11"
					/>
				</div>

				{/* Notes */}
				<div className="space-y-2">
					<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.notes", "Notes")}</Label>
					<Textarea
						value={workforce.notes || ""}
						onChange={(e) => onChange("notes", e.target.value)}
						placeholder={t("workforce.placeholders.notes", "Additional notes...")}
						rows={4}
						className="resize-none"
					/>
				</div>
			</TabsContent>

			{/* Financial Tab */}
			<TabsContent value="financial" className="space-y-5">
				{/* Billing Type & Currency */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.billingType", "Billing Type")}</Label>
						<Select
							value={workforce.billing_type || ""}
							onValueChange={(value) => onChange("billing_type", value)}
						>
							<SelectTrigger className="h-11">
								<SelectValue placeholder={t("workforce.placeholders.selectBilling", "Select...")} />
							</SelectTrigger>
							<SelectContent>
								{BILLING_TYPES.map((type) => (
									<SelectItem key={type.value} value={type.value}>
										{type.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="space-y-2">
						<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.currency", "Currency")}</Label>
						<Select
							value={workforce.currency || "EUR"}
							onValueChange={(value) => onChange("currency", value)}
						>
							<SelectTrigger className="h-11">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{CURRENCY_OPTIONS.map((currency) => (
									<SelectItem key={currency} value={currency}>
										{currency}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				{/* Daily Rates */}
				<Card className="border-blue-200 bg-blue-50/50">
					<CardContent className="pt-4">
						<div className="flex items-center gap-2 mb-4">
							<DollarSign className="h-4 w-4 text-blue-500" />
							<span className="text-sm font-semibold text-blue-800">{t("workforce.fields.dailyRates", "Daily Rates")}</span>
						</div>
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
							<div className="space-y-2">
								<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.purchasePrice", "Purchase Price")}</Label>
								<Input
									type="number"
									min="0"
									step="0.01"
									value={workforce.purchase_price_daily || ""}
									onChange={(e) => onChange("purchase_price_daily", e.target.value ? parseFloat(e.target.value) : null)}
									placeholder="0.00"
									className="h-11"
								/>
								<p className="text-xs text-muted-foreground">{t("workforce.fields.purchasePriceHint", "What you pay the consultant")}</p>
							</div>
							<div className="space-y-2">
								<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.sellingPrice", "Selling Price")}</Label>
								<Input
									type="number"
									min="0"
									step="0.01"
									value={workforce.selling_price_daily || ""}
									onChange={(e) => onChange("selling_price_daily", e.target.value ? parseFloat(e.target.value) : null)}
									placeholder="0.00"
									className="h-11"
								/>
								<p className="text-xs text-muted-foreground">{t("workforce.fields.sellingPriceHint", "What you charge the client")}</p>
							</div>
							<div className="space-y-2">
								<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.margin", "Margin")}</Label>
								<div className={cn(
									"h-11 flex items-center justify-center rounded-sm border font-semibold text-lg bg-muted"
								)}>
									{dailyMargin !== null ? `${dailyMargin.toFixed(1)}%` : '-'}
								</div>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Hourly Rates */}
				<Card>
					<CardContent className="pt-4">
						<div className="flex items-center gap-2 mb-4">
							<DollarSign className="h-4 w-4 text-muted-foreground" />
							<span className="text-sm font-semibold">{t("workforce.fields.hourlyRates", "Hourly Rates")}</span>
						</div>
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
							<div className="space-y-2">
								<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.purchasePrice", "Purchase Price")}</Label>
								<Input
									type="number"
									min="0"
									step="0.01"
									value={workforce.purchase_price_hourly || ""}
									onChange={(e) => onChange("purchase_price_hourly", e.target.value ? parseFloat(e.target.value) : null)}
									placeholder="0.00"
									className="h-11"
								/>
							</div>
							<div className="space-y-2">
								<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.sellingPrice", "Selling Price")}</Label>
								<Input
									type="number"
									min="0"
									step="0.01"
									value={workforce.selling_price_hourly || ""}
									onChange={(e) => onChange("selling_price_hourly", e.target.value ? parseFloat(e.target.value) : null)}
									placeholder="0.00"
									className="h-11"
								/>
							</div>
							<div className="space-y-2">
								<Label className="text-sm font-medium text-gray-700">{t("workforce.fields.margin", "Margin")}</Label>
								<div className="h-11 flex items-center justify-center rounded-sm border font-semibold text-lg bg-muted">
									{hourlyMargin !== null ? `${hourlyMargin.toFixed(1)}%` : '-'}
								</div>
							</div>
						</div>
					</CardContent>
				</Card>
			</TabsContent>
		</Tabs>
	);
};
